import React from 'react'
import styled, { css } from 'styled-components'
import uniqueId from 'lodash/fp/uniqueId'
import { BodyStyle } from '../typography/styles'
import {
  TriStateCheckboxValue,
  TriStateCheckboxValues,
} from './tri-state-checkbox'
import { Icon } from '../icon'

export type CheckboxValue = boolean | TriStateCheckboxValue

export interface BaseCheckboxProps {
  onClick: () => void
  id?: string
  value?: CheckboxValue
  disabled?: boolean
  label?: string | React.ReactNode
}

const PrimaryFocusStyle = css`
  box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
    0 0 0 3px ${({ theme }) => theme.color.primary70};
`

const UncheckedCheckbox = css`
  border-color: ${({ theme }) => theme.color.grey100};
`

const DisabledUncheckedCheckbox = css`
  border-color: ${({ theme }) => theme.color.grey30};
`

const CheckedAndMixedCheckbox = css`
  background-color: ${({ theme }) => theme.color.primary110};
  border-color: ${({ theme }) => theme.color.primary110};
  color: ${({ theme }) => theme.color.primaryButtonText};

  &:hover {
    background-color: ${({ theme }) => theme.color.primary120};
    border-color: ${({ theme }) => theme.color.primary120};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.primary130};
    border-color: ${({ theme }) => theme.color.primary130};
  }
`

const DisabledCheckedAndMixedCheckbox = css`
  background-color: ${({ theme }) => theme.color.primary30};
  border-color: ${({ theme }) => theme.color.primary30};
  color: ${({ theme }) => theme.color.primaryButtonText};
`

const Disabled = css<{ $value?: CheckboxValue }>`
  cursor: not-allowed;

  ${({ $value }) =>
    $value === true ||
    $value === TriStateCheckboxValues.CHECKED ||
    $value === TriStateCheckboxValues.MIXED
      ? DisabledCheckedAndMixedCheckbox
      : DisabledUncheckedCheckbox};
`

const Enabled = css<{ $value?: CheckboxValue }>`
  cursor: pointer;

  &.focus-visible {
    ${PrimaryFocusStyle};
  }

  ${({ $value }) =>
    $value === true ||
    $value === TriStateCheckboxValues.CHECKED ||
    $value === TriStateCheckboxValues.MIXED
      ? CheckedAndMixedCheckbox
      : UncheckedCheckbox};
`

export const StyledCheckbox = styled.div<{
  $disabled?: boolean
  $value?: CheckboxValue
}>`
  width: ${({ theme }) => theme.space.s20};
  height: ${({ theme }) => theme.space.s20};
  border: ${({ theme }) => theme.space.s2} solid;
  margin: ${({ theme }) => theme.space.s2};

  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.space.s2};
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  cursor: pointer;
  user-select: none;

  ${({ $disabled }) => ($disabled ? Disabled : Enabled)};
`
export const CheckboxWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  width: fit-content;
  outline: none;
  margin: ${({ theme }) => theme.space.s8} 0;

  &.focus-visible > div {
    ${({ $disabled }) => !$disabled && PrimaryFocusStyle};
  }
`

const CheckBoxLabel = styled.label<{ $disabled?: boolean }>`
  ${BodyStyle};
  padding: 0 ${({ theme }) => theme.space.s16};
  user-select: none;
  word-break: normal;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed;' : 'pointer')};
`
const HiddenInput = styled.input<{ $disabled?: boolean }>`
  border: 0px;
  clip: rect(0px, 0px, 0px, 0px);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0px;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;

  &.focus-visible + div {
    ${({ $disabled }) => !$disabled && PrimaryFocusStyle};
  }
`

export const BaseCheckbox: React.FC<BaseCheckboxProps> = ({
  value,
  disabled,
  label,
  id,
  onClick,
  ...props
}) => {
  const isChecked = value === true || value === TriStateCheckboxValues.CHECKED

  const checkboxId = id || uniqueId('checkbox-')

  return (
    <CheckboxWrapper {...props} $disabled={disabled}>
      <HiddenInput
        id={checkboxId}
        type="checkbox"
        data-testid="hidden-input"
        onChange={onClick}
        checked={isChecked}
        $disabled={disabled}
      />
      <StyledCheckbox
        data-testid="checkbox-styled"
        $value={value}
        $disabled={disabled}
        onClick={onClick}
      >
        {value === TriStateCheckboxValues.MIXED && (
          <Icon type="remove" size="s16" />
        )}
        {isChecked && <Icon type="check" size="s16" />}
      </StyledCheckbox>
      {label && (
        <CheckBoxLabel
          className={checkboxId}
          htmlFor={checkboxId}
          $disabled={disabled}
        >
          {label}
        </CheckBoxLabel>
      )}
    </CheckboxWrapper>
  )
}
